import { useMutation } from "@tanstack/react-query";
import { privateApi } from "../../../api/api";
import { UpdatePropertyType } from "../schema/UpdatePropertySchema";
import { ValidationUpdateResponse } from "../response/ValidationUpdateResponse";
import { ResponseSchema } from "../response/PropertyUpdateResponse";

export const useUpdateProperty = () => {
    return useMutation({
        mutationFn: async (formData: UpdatePropertyType) => {
            const safeAppend = (key: string, value: any) => {
                if (value !== undefined) {
                    newFormData.append(key, value === null ? "" : value);
                }
            };

            const newFormData = new FormData();

            // Required Fields
            newFormData.append("property_type", formData.property_type);
            newFormData.append("name", formData.name);
            newFormData.append("preferred_currency", formData.preferred_currency);
            newFormData.append("time_zone", formData.time_zone);
            newFormData.append("primary_email", formData.primary_email);

            // Optional Fields
            // if (formData.primary_phone_country_code) newFormData.append("primary_phone_country_code", formData.primary_phone_country_code);
            // if (formData.primary_phone) newFormData.append("primary_phone", formData.primary_phone);
            // if (formData.postal_code) newFormData.append("postal_code", formData.postal_code);
            // if (formData.country) newFormData.append("country", formData.country);
            // if (formData.state) newFormData.append("state", formData.state);
            // if (formData.city) newFormData.append("city", formData.city);
            // if (formData.street_address) newFormData.append("street_address", formData.street_address);
            // if (formData.longitude) newFormData.append("longitude", formData.longitude);
            // if (formData.latitude) newFormData.append("latitude", formData.latitude);
            // if (formData.description) newFormData.append("description", formData.description);
            // if (formData.website) newFormData.append("website", formData.website);
            safeAppend("primary_phone_country_code", formData.primary_phone_country_code);
            safeAppend("primary_phone", formData.primary_phone);
            safeAppend("postal_code", formData.postal_code);
            safeAppend("country", formData.country);
            safeAppend("state", formData.state);
            safeAppend("city", formData.city);
            safeAppend("street_address", formData.street_address);
            safeAppend("longitude", formData.longitude);
            safeAppend("latitude", formData.latitude);
            safeAppend("description", formData.description);
            safeAppend("website", formData.website);
            safeAppend("term_condition", formData.term_condition);
            safeAppend("cancellation_policy", formData.cancellation_policy);
            safeAppend("region_id", formData.region_id);

            const { format, parse } = await import("date-fns");
            const formatTime = (timeStr: string | null | undefined) => {
                if (!timeStr) return null;
                try {
                    return format(parse(timeStr, "HH:mm", new Date()), "hh:mm a").toLowerCase();
                } catch (e) {
                    return null;
                }
            };

            const checkinStart = formatTime(formData.checkin_time_start);
            const checkinEnd = formatTime(formData.checkin_time_end);
            if (checkinStart && checkinEnd) {
                safeAppend("checkin_time", `${checkinStart} - ${checkinEnd}`);
            } else if (checkinStart) {
                safeAppend("checkin_time", checkinStart);
            }

            const checkoutStart = formatTime(formData.checkout_time_start);
            const checkoutEnd = formatTime(formData.checkout_time_end);
            if (checkoutStart && checkoutEnd) {
                safeAppend("checkout_time", `${checkoutStart} - ${checkoutEnd}`);
            } else if (checkoutStart) {
                safeAppend("checkout_time", checkoutStart);
            }

            // Handle array of emails
            if (formData.cc_emails) {
                formData.cc_emails.forEach((email, index) => {
                    newFormData.append(`cc_emails[${index}]`, email);
                });
            }

            if (formData.accounting_email) {
                newFormData.append("accounting_email", formData.accounting_email);
            }

            if (formData.mobile_number_country_code) {
                newFormData.append("mobile_number_country_code", formData.mobile_number_country_code);
            }

            if (formData.mobile_number) {
                newFormData.append("mobile_number", formData.mobile_number);
            }

            if (formData.enable_cvv !== null) {
                newFormData.append("enable_cvv", String(formData.enable_cvv));
            }

            // Handle Logo (File Upload)
            if (formData.logo instanceof File) {
                newFormData.append("logo", formData.logo);
            }

            newFormData.append("_method", "PUT");

            return await privateApi.post(`/v3/edit-property`, newFormData, {
                headers: { "Content-Type": "multipart/form-data" },
            });
        },
        onSuccess: async (response) => {
            try {
                const validationResult = ResponseSchema.safeParse(response.data);

                if (!validationResult.success) {
                    console.error("Validation failed:", validationResult.error.errors);
                    throw new Error("Invalid update property data format");
                }

                return validationResult.data;
            } catch (error) {
                console.error('Failed to update property', error);
                throw error;
            }
        },
        onError: (error) => {
            const validationResult = ValidationUpdateResponse.safeParse((error as any).response.data);

            if (!validationResult.success) {
                console.error("Validation failed:", validationResult.error.errors);
                throw new Error("Invalid property data format");
            } else {
                console.error('Failed to update property', error);
                throw error;
            }
        },
    });
};